import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { writeFetch, logout } from '../api';
import { getAgeBand } from '../utils/ageBands';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTrash2, FiLoader, FiLogOut, FiArrowRight, FiActivity, FiX } = FiIcons;

export default function ParentDashboard() {
  const { state, dispatch } = useGame();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKid, setNewKid] = useState({ name: '', birthday: '', password: '' });

  const fetchData = useCallback(async () => {
    if (!state.user) return;
    try {
      const res = await writeFetch(`read/kids?owner_id=${state.user.id}`);
      const result = await res.json();
      if (result.status === 'success') {
        setKids(result.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [state.user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddKid = async (e) => {
    e.preventDefault();
    
    // Clean data before saving
    const cleanName = newKid.name.trim();
    const cleanPassword = newKid.password.trim();
    const age = new Date().getFullYear() - new Date(newKid.birthday).getFullYear();
    
    const kidPayload = {
      id: crypto.randomUUID(),
      owner_id: state.user.id,
      name: cleanName,
      password: cleanPassword,
      birthday: newKid.birthday,
      age: age,
      total_tokens: 100,
      completed_quests: '[]',
      game_stats: JSON.stringify({ totalAnswers: 0, correctAnswers: 0 }),
      avatar_image: 'lion'
    };

    try {
      await writeFetch('create/kids', {
        method: 'POST',
        body: JSON.stringify(kidPayload),
      });
      setShowAddModal(false);
      setNewKid({ name: '', birthday: '', password: '' });
      fetchData();
    } catch (err) {
      alert("Failed to add explorer. Try a different name!");
    }
  };

  const loginAsKid = (kid) => {
    localStorage.setItem('active_kid', JSON.stringify({ ...kid, isKid: true }));
    dispatch({ type: 'KID_AUTH_SUCCESS', payload: kid });
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><SafeIcon icon={FiLoader} className="text-5xl text-blue-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 p-6 pt-24">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-kid text-slate-800">Parent Dashboard</h1>
            <p className="font-comic text-slate-500">Manage your little explorers</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-green-500 text-white px-6 py-3 rounded-2xl font-kid flex items-center gap-2 hover:bg-green-600 shadow-lg"
            >
              <SafeIcon icon={FiPlus} /> Add Explorer
            </button>
            <button onClick={handleLogout} className="bg-red-50 text-red-500 p-3 rounded-2xl hover:bg-red-100">
              <SafeIcon icon={FiLogOut} className="text-2xl" />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {kids.map((kid) => {
            const stats = JSON.parse(kid.game_stats || '{}');
            const accuracy = stats.totalAnswers > 0 ? Math.round((stats.correctAnswers / stats.totalAnswers) * 100) : 0;
            const band = getAgeBand(kid.age);

            return (
              <motion.div 
                key={kid.id}
                whileHover={{ y: -5 }}
                className="bg-white rounded-[2.5rem] p-8 shadow-xl border-4 border-slate-100"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-3xl flex items-center justify-center text-4xl bg-gradient-to-br ${band.color} text-white`}>
                    {kid.avatar_image === 'lion' ? '🦁' : '🐯'}
                  </div>
                  <div>
                    <h3 className="font-kid text-xl text-slate-800">{kid.name}</h3>
                    <p className="font-comic text-sm text-slate-400">Age {kid.age} · {band.label}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-yellow-50 p-4 rounded-2xl text-center">
                    <div className="text-2xl">🪙</div>
                    <div className="font-kid text-slate-700">{kid.total_tokens}</div>
                    <div className="text-[10px] font-comic text-slate-400 uppercase">Tokens</div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-2xl text-center">
                    <div className="text-2xl">🎯</div>
                    <div className="font-kid text-slate-700">{accuracy}%</div>
                    <div className="text-[10px] font-comic text-slate-400 uppercase">Accuracy</div>
                  </div>
                </div>

                <button 
                  onClick={() => loginAsKid(kid)}
                  className="w-full bg-jungle-500 text-white py-4 rounded-2xl font-kid flex items-center justify-center gap-2 hover:bg-jungle-600 shadow-lg"
                >
                  Enter Game <SafeIcon icon={FiArrowRight} />
                </button>
              </motion.div>
            );
          })}

          {kids.length === 0 && (
            <div className="col-span-full py-20 text-center bg-white rounded-[3rem] border-4 border-dashed border-slate-200">
              <div className="text-6xl mb-4">🏜️</div>
              <p className="font-comic text-slate-400">No explorers added yet. Click "Add Explorer" to start!</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Kid Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAddModal(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-white rounded-[3rem] p-10 max-w-md w-full shadow-2xl border-4 border-green-200"
            >
              <button onClick={() => setShowAddModal(false)} className="absolute top-6 right-6 text-slate-400"><SafeIcon icon={FiX} /></button>
              <h2 className="text-2xl font-kid text-slate-800 mb-6">New Explorer</h2>
              <form onSubmit={handleAddKid} className="space-y-4">
                <input
                  type="text"
                  placeholder="Explorer's Name"
                  required
                  value={newKid.name}
                  onChange={(e) => setNewKid({...newKid, name: e.target.value})}
                  className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-comic"
                />
                <input
                  type="date"
                  required
                  value={newKid.birthday}
                  onChange={(e) => setNewKid({...newKid, birthday: e.target.value})}
                  className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-comic"
                />
                <input
                  type="password"
                  placeholder="Secret Password"
                  required
                  value={newKid.password}
                  onChange={(e) => setNewKid({...newKid, password: e.target.value})}
                  className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl font-comic"
                />
                <button type="submit" className="w-full bg-green-500 text-white py-4 rounded-2xl font-kid text-lg shadow-lg">
                  Create Profile! 🚀
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}